import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ExternalLink, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { API } from '../App';
import { useIsland } from '../contexts/IslandContext';

// Helper to detect if URL is a video file
const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.mov', '.m4v'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext));
};

// Check if user prefers reduced motion
const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Check if user is in data saver mode
const isDataSaverMode = () => {
  if (typeof navigator === 'undefined') return false;
  return navigator.connection?.saveData === true;
};

// Reusable Video Banner component with all optimizations
const VideoBanner = ({ src, mobileSrc, poster, alt, className = '', onError }) => {
  const videoRef = useRef(null);
  const [showVideo, setShowVideo] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Determine which source to use (mobile vs desktop)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const videoSrc = mobileSrc && isMobile ? mobileSrc : src;
  
  // Check for reduced motion / data saver preferences
  useEffect(() => {
    if (prefersReducedMotion() || isDataSaverMode()) {
      setShowVideo(false);
    }
  }, []);
  
  // Handle video loading and autoplay
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !showVideo) return;
    
    const handleCanPlay = () => {
      setIsLoaded(true);
      // Try to play when loaded
      video.play().catch((err) => {
        console.log('Video autoplay blocked:', err.message);
        // Don't set showVideo to false - keep trying with muted
      });
    };
    
    const handleError = () => {
      setVideoError(true);
    };
    
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    
    // Also try to play immediately for browsers that support it
    if (video.readyState >= 3) {
      handleCanPlay();
    }
    
    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
    };
  }, [showVideo, videoSrc]);
  
  // Lazy loading with IntersectionObserver - only pause/play based on visibility
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !showVideo) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    
    observer.observe(video);
    return () => observer.disconnect();
  }, [showVideo]);
  
  // If reduced motion, data saver, or video error - show poster/fallback
  if (!showVideo || videoError) {
    if (poster) {
      return (
        <img
          src={poster}
          alt={alt}
          className={className}
          onError={onError}
        />
      );
    }
    // No poster - show gradient placeholder
    return (
      <div className={`${className} bg-gradient-to-br from-gray-800 to-gray-900`} />
    );
  }
  
  return (
    <video
      ref={videoRef}
      className={`${className} pointer-events-none`}
      autoPlay
      loop
      muted
      playsInline
      webkit-playsinline="true"
      x-webkit-airplay="deny"
      disablePictureInPicture
      preload="auto"
      poster={poster}
    >
      <source src={videoSrc} type="video/mp4" />
      <source src={videoSrc.replace('.mp4', '.webm')} type="video/webm" />
    </video>
  );
};

// Custom hook for swipe gesture detection with tap vs swipe distinction
const useSwipeGesture = (onSwipeLeft, onSwipeRight, onInteraction) => {
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchEndX = useRef(0);
  const touchEndY = useRef(0);
  const touchStartTime = useRef(0);
  const isDragging = useRef(false);
  const didSwipe = useRef(false); // Track if a swipe occurred (to block link clicks)
  
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    isDragging.current = true;
    didSwipe.current = false;
  }, []);
  
  const handleTouchMove = useCallback((e) => {
    if (!isDragging.current) return;
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
    
    // Check if this is becoming a horizontal swipe (to prevent vertical scroll interference)
    const diffX = Math.abs(touchStartX.current - touchEndX.current);
    const diffY = Math.abs(touchStartY.current - touchEndY.current);
    
    // If horizontal movement is significantly more than vertical, mark as potential swipe
    if (diffX > 15 && diffX > diffY * 1.5) {
      didSwipe.current = true;
    }
  }, []);
  
  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    
    const diffX = touchStartX.current - touchEndX.current;
    const diffY = Math.abs(touchStartY.current - touchEndY.current);
    const touchDuration = Date.now() - touchStartTime.current;
    const threshold = 50; // Minimum swipe distance
    
    // Only count as swipe if:
    // 1. Horizontal movement > threshold
    // 2. Horizontal movement > vertical movement (not scrolling)
    // 3. Not a quick tap (duration > 100ms or moved significantly)
    const isHorizontalSwipe = Math.abs(diffX) > threshold && Math.abs(diffX) > diffY;
    const isNotQuickTap = touchDuration > 100 || Math.abs(diffX) > 20;
    
    if (isHorizontalSwipe && isNotQuickTap) {
      didSwipe.current = true;
      // Notify parent that user interacted (to pause timer)
      onInteraction?.();
      
      if (diffX > 0) {
        onSwipeLeft?.();
      } else {
        onSwipeRight?.();
      }
    }
  }, [onSwipeLeft, onSwipeRight, onInteraction]);
  
  // Handler to check if a click should be blocked (because it was a swipe)
  const handleClick = useCallback((e) => {
    if (didSwipe.current) {
      e.preventDefault();
      e.stopPropagation();
      didSwipe.current = false;
    }
  }, []);
  
  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onClickCapture: handleClick, // Use capture phase to intercept before link click
    didSwipe
  };
};

// Utility function to track ad clicks with placement context
const trackAdClick = async (adId, placementId = null) => {
  if (!adId) return;
  try {
    const url = placementId 
      ? `${API}/banners/${adId}/click?placement_id=${encodeURIComponent(placementId)}`
      : `${API}/banners/${adId}/click`;
    await fetch(url, { method: 'POST' });
  } catch (error) {
    // Silent fail - don't disrupt user experience
    console.error('Failed to track ad click:', error);
  }
};

// Utility function to track ad impressions (views) with placement context
const trackAdImpression = async (adId, placementId = null) => {
  if (!adId) return;
  try {
    const url = placementId
      ? `${API}/banners/${adId}/impression?placement_id=${encodeURIComponent(placementId)}`
      : `${API}/banners/${adId}/impression`;
    await fetch(url, { method: 'POST' });
  } catch (error) {
    // Silent fail - don't disrupt user experience
    console.error('Failed to track ad impression:', error);
  }
};

// Custom hook to track impression once per ad with placement context
const useImpressionTracker = (adId, placementId = null, isVisible = true) => {
  const trackedRef = useRef(new Set());
  
  useEffect(() => {
    const trackKey = `${adId}-${placementId || 'default'}`;
    if (adId && isVisible && !trackedRef.current.has(trackKey)) {
      trackedRef.current.add(trackKey);
      trackAdImpression(adId, placementId);
    }
  }, [adId, placementId, isVisible]);
};

// Utility function to get overlay opacity (handles both 0-1 and 0-100 ranges)
const getOverlayOpacity = (value) => {
  if (value === undefined || value === null) return 0.5;
  // If value is > 1, assume it's a percentage (0-100)
  if (value > 1) return value / 100;
  return value;
};

// Get button styles based on button_style and button_color
const getButtonStyles = (ad) => {
  const buttonStyle = ad.button_style || 'filled';
  const buttonColor = ad.button_color || '#14b8a6';
  
  if (buttonStyle === 'outline') {
    return {
      backgroundColor: 'transparent',
      border: `2px solid ${buttonColor}`,
      color: buttonColor === '#FFFFFF' ? '#FFFFFF' : buttonColor
    };
  }
  return {
    backgroundColor: buttonColor,
    color: buttonColor === '#FFFFFF' || buttonColor === '#F59E0B' ? '#000000' : '#FFFFFF'
  };
};

// Get text size classes
const getTextSizeClasses = (textSize, isLarge = false) => {
  if (isLarge) {
    switch (textSize) {
      case 'small': return 'text-xl md:text-2xl';
      case 'large': return 'text-3xl md:text-4xl';
      default: return 'text-2xl md:text-3xl';
    }
  }
  switch (textSize) {
    case 'small': return 'text-base';
    case 'large': return 'text-xl';
    default: return 'text-lg';
  }
};

// Small Ad Block Component (single ad)
const SmallPromoBlock = ({ ad, isVisible = true, placementId = null }) => {
  useImpressionTracker(ad?.id, placementId, isVisible);
  
  // Check per-placement visibility settings
  const placementSettings = ad.placement_settings?.[placementId] || {};
  const showText = placementSettings.show_text !== false;
  const showButton = placementSettings.show_button !== false;
  
  const overlayOpacity = getOverlayOpacity(ad.overlay_opacity);
  const buttonStyles = getButtonStyles(ad);
  const textSizeClass = getTextSizeClasses(ad.text_size, false);
  
  const handleClick = (e) => {
    if (!ad.link_url || ad.link_url === '#') {
      e.preventDefault();
    } else {
      trackAdClick(ad.id, placementId);
    }
  };

  return (
    <a
      href={ad.link_url || '#'}
      onClick={handleClick}
      target={ad.link_url && ad.link_url !== '#' ? '_blank' : undefined}
      rel={ad.link_url && ad.link_url !== '#' ? 'noopener noreferrer' : undefined}
      className={`promo-block promo-block-small group relative overflow-hidden rounded-2xl block aspect-[2/1] transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full absolute'
      }`}
      data-testid={`ad-small-${ad.id}`}
    >
      {/* Background */}
      <div 
        className="absolute inset-0"
        style={{ backgroundColor: ad.background_color || '#1a1a2e' }}
      >
        {ad.image_url && (
          <>
            {/* Check if it's a video or image */}
            {isVideoUrl(ad.image_url) || isVideoUrl(ad.mobile_image_url) ? (
              <VideoBanner
                src={ad.image_url}
                mobileSrc={ad.mobile_image_url}
                poster={ad.poster_url || (!isVideoUrl(ad.image_url) ? ad.image_url : null)}
                alt={ad.display_text || ad.title || 'Sponsored'}
                className="w-full h-full object-cover"
                onError={(e) => e.target.style.display = 'none'}
              />
            ) : (
              <picture>
                {ad.mobile_image_url && (
                  <source media="(max-width: 767px)" srcSet={ad.mobile_image_url} />
                )}
                <img
                  src={ad.image_url}
                  alt={ad.display_text || ad.title || 'Sponsored'}
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </picture>
            )}
            {overlayOpacity > 0 && (
              <div 
                className="absolute inset-0"
                style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}
              />
            )}
          </>
        )}
      </div>
      
      {/* Content */}
      <div className={`absolute bottom-0 left-0 right-0 p-4 flex flex-col ${
        ad.text_position === 'left' || ad.text_position?.includes('left') ? 'items-start text-left pl-4 pr-12' :
        ad.text_position === 'right' || ad.text_position?.includes('right') ? 'items-end text-right pr-4 pl-12' :
        'items-center text-center px-4'
      }`}>
        {showText && (ad.display_text || ad.description) && (
          <>
            {ad.display_text && (
              <h4 
                className={`font-bold ${textSizeClass} mb-0.5`}
                style={{ color: ad.text_color || '#fff' }}
              >
                {ad.display_text}
              </h4>
            )}
            {ad.description && (
              <p className="text-sm mb-3 opacity-80" style={{ color: ad.text_color || '#fff' }}>
                {ad.description}
              </p>
            )}
          </>
        )}
        {showButton && ad.button_text && (
          <span 
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold shadow-lg"
            style={buttonStyles}
          >
            {ad.button_text}
            <ExternalLink className="w-3.5 h-3.5" />
          </span>
        )}
      </div>
    </a>
  );
};

// Small Ad Carousel - Shows 2 ads, rotates one at a time every 3 seconds
const SmallPromoCarousel = ({ ads, count = 2, placementId = 'sidebar' }) => {
  const [currentIndices, setCurrentIndices] = useState([0, 1]);
  const [rotatingSlot, setRotatingSlot] = useState(null); // 0 or 1
  const [isPaused, setIsPaused] = useState(false);

  // Rotate one ad at a time every 3 seconds
  useEffect(() => {
    if (isPaused || ads.length <= count) return;

    const interval = setInterval(() => {
      setRotatingSlot(prev => (prev === null || prev === 1) ? 0 : 1);
      
      setCurrentIndices(prev => {
        const slotToRotate = (rotatingSlot === null || rotatingSlot === 1) ? 0 : 1;
        const newIndices = [...prev];
        // Find next ad that's not currently showing
        let nextIndex = (prev[slotToRotate] + count) % ads.length;
        while (prev.includes(nextIndex) && ads.length > count) {
          nextIndex = (nextIndex + 1) % ads.length;
        }
        newIndices[slotToRotate] = nextIndex;
        return newIndices;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [ads.length, count, isPaused, rotatingSlot]);

  if (ads.length === 0) return null;

  // Ensure we have enough ads to display
  const displayAds = [];
  for (let i = 0; i < count; i++) {
    const adIndex = currentIndices[i] !== undefined ? currentIndices[i] : i;
    displayAds.push(ads[adIndex % ads.length]);
  }

  return (
    <div 
      className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {displayAds.map((ad, idx) => (
        <div key={`slot-${idx}-${ad.id}`} className="relative overflow-hidden">
          <SmallPromoBlock ad={ad} isVisible={true} placementId={placementId} />
        </div>
      ))}
    </div>
  );
};

// Large Ad Slide Component
const LargePromoSlide = ({ ad, index, placementId = 'home_top' }) => {
  useImpressionTracker(ad?.id, placementId, true);
  
  // Check per-placement visibility settings
  const placementSettings = ad.placement_settings?.[placementId] || {};
  const showText = placementSettings.show_text !== false;
  const showButton = placementSettings.show_button !== false;
  
  const overlayOpacity = getOverlayOpacity(ad.overlay_opacity);
  const buttonStyles = getButtonStyles(ad);
  const textSizeClass = getTextSizeClasses(ad.text_size, true);
  
  const handleClick = (e) => {
    if (!ad.link_url || ad.link_url === '#') {
      e.preventDefault();
    } else {
      trackAdClick(ad.id, placementId);
    }
  };

  return (
    <a
      href={ad.link_url || '#'}
      onClick={handleClick}
      target={ad.link_url && ad.link_url !== '#' ? '_blank' : undefined}
      rel={ad.link_url && ad.link_url !== '#' ? 'noopener noreferrer' : undefined}
      className="promo-block promo-block-large group relative overflow-hidden rounded-2xl block aspect-[2/1] md:aspect-[6/1]"
      data-testid={`ad-large-${index}`}
    >
      {/* Background */}
      <div 
        className="absolute inset-0"
        style={{ backgroundColor: ad.background_color || '#1a1a2e' }}
      >
        {ad.image_url && (
          <>
            {/* Check if it's a video or image */}
            {isVideoUrl(ad.image_url) || isVideoUrl(ad.mobile_image_url) ? (
              <VideoBanner
                src={ad.image_url}
                mobileSrc={ad.mobile_image_url}
                poster={ad.poster_url || (!isVideoUrl(ad.image_url) ? ad.image_url : null)}
                alt={ad.display_text || ad.title || 'Sponsored'}
                className="w-full h-full object-cover"
                onError={(e) => e.target.style.display = 'none'}
              />
            ) : (
              <picture>
                {ad.mobile_image_url && (
                  <source media="(max-width: 767px)" srcSet={ad.mobile_image_url} />
                )}
                <img
                  src={ad.image_url}
                  alt={ad.display_text || ad.title || 'Sponsored'}
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </picture>
            )}
            {overlayOpacity > 0 && (
              <div 
                className="absolute inset-0"
                style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}
              />
            )}
          </>
        )}
        {overlayOpacity > 0 && (
          <div 
            className="absolute inset-0 bg-gradient-to-r from-black via-black/50 to-transparent" 
            style={{ opacity: Math.min(overlayOpacity * 1.4, 0.8) }}
          />
        )}
      </div>
      
      {/* Content */}
      <div className={`absolute bottom-0 left-0 right-0 p-6 md:p-8 flex flex-col ${
        ad.text_position === 'left' || ad.text_position?.includes('left') ? 'items-start text-left pl-6 md:pl-10 pr-[40%]' :
        ad.text_position === 'right' || ad.text_position?.includes('right') ? 'items-end text-right pr-6 md:pr-10 pl-[40%]' :
        ad.text_position?.includes('center') ? 'items-center text-center px-6 md:px-10' :
        'items-start text-left pl-6 md:pl-10 pr-[40%]'
      }`}>
        {showText && (ad.display_text || ad.description) && (
          <>
            {ad.display_text && (
              <h3 
                className={`font-bold ${textSizeClass} mb-2`}
                style={{ color: ad.text_color || '#fff' }}
              >
                {ad.display_text}
              </h3>
            )}
            {ad.description && (
              <p 
                className="text-sm md:text-base mb-5 max-w-md line-clamp-2 opacity-90"
                style={{ color: ad.text_color || '#fff' }}
              >
                {ad.description}
              </p>
            )}
          </>
        )}
        {showButton && ad.button_text && (
          <span 
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold shadow-xl"
            style={buttonStyles}
          >
            {ad.button_text}
            <ExternalLink className="w-4 h-4" />
          </span>
        )}
      </div>
    </a>
  );
};

// Large Ad Carousel Component with swipe support
const LargePromoCarousel = ({ ads, placementId = 'home_top' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const pauseTimeoutRef = useRef(null);

  useEffect(() => {
    if (isPaused || userInteracted || ads.length <= 1) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % ads.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [ads.length, isPaused, userInteracted]);

  // Reset userInteracted after 8 seconds of no interaction
  useEffect(() => {
    if (userInteracted) {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
      pauseTimeoutRef.current = setTimeout(() => {
        setUserInteracted(false);
      }, 8000);
    }
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, [userInteracted]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setUserInteracted(true);
  };
  
  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + ads.length) % ads.length);
    setUserInteracted(true);
  }, [ads.length]);
  
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % ads.length);
    setUserInteracted(true);
  }, [ads.length]);

  // Callback for when user swipes - pause auto-rotation
  const handleSwipeInteraction = useCallback(() => {
    setUserInteracted(true);
  }, []);

  // Swipe gesture support with interaction callback
  const swipeHandlers = useSwipeGesture(goToNext, goToPrev, handleSwipeInteraction);

  if (ads.length === 0) return null;

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div 
        className="overflow-hidden rounded-2xl relative touch-pan-y"
        {...swipeHandlers}
      >
        <div 
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {ads.map((ad, index) => (
            <div key={ad.id} className="flex-none w-full min-w-0">
              <LargePromoSlide ad={ad} index={index} placementId={placementId} />
            </div>
          ))}
        </div>
      </div>

      {ads.length > 1 && (
        <>
          {/* Navigation arrows - hidden on mobile, only show on desktop hover */}
          <button
            onClick={goToPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/20 backdrop-blur-md text-white hidden md:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white/30 hover:scale-110 z-10"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/20 backdrop-blur-md text-white hidden md:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white/30 hover:scale-110 z-10"
            aria-label="Next slide"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Dot navigation */}
          <div className="flex items-center justify-center gap-2 mt-4 relative z-20">
            {ads.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  index === currentIndex 
                    ? 'w-8 h-2.5 bg-primary shadow-lg shadow-primary/30' 
                    : 'w-2.5 h-2.5 bg-muted-foreground/40 hover:bg-muted-foreground/60'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Compact Ad for MegaMenu (smaller format)
export const MegaMenuPromo = ({ category }) => {
  const [ad, setAd] = useState(null);
  const { currentIsland } = useIsland();

  useEffect(() => {
    const abortController = new AbortController();
    
    // Reset state when category changes to prevent stale ads
    setAd(null);

    const fetchAd = async () => {
      try {
        const params = new URLSearchParams();
        params.append('position', 'megamenu');
        if (currentIsland) {
          params.append('island', currentIsland);
        }
        // Pass category for targeted ads
        if (category) {
          params.append('category', category);
        }
        
        const response = await fetch(`${API}/banners?${params}`, {
          signal: abortController.signal
        });
        if (response.ok) {
          const data = await response.json();
          const activeAds = data.filter(ad => ad.active);
          if (activeAds.length > 0) {
            // Rotate through ads randomly for variety
            setAd(activeAds[Math.floor(Math.random() * activeAds.length)]);
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error fetching megamenu ad:', error);
      }
    };

    fetchAd();
    
    return () => abortController.abort();
  }, [currentIsland, category]);

  // Track impression when ad is loaded
  useImpressionTracker(ad?.id, 'category_sidebar', !!ad);

  if (!ad) return null;

  const handleClick = (e) => {
    if (!ad.link_url || ad.link_url === '#') {
      e.preventDefault();
    } else {
      trackAdClick(ad.id, 'category_sidebar');
    }
  };

  return (
    <a
      href={ad.link_url || '#'}
      onClick={handleClick}
      target={ad.link_url && ad.link_url !== '#' ? '_blank' : undefined}
      rel={ad.link_url && ad.link_url !== '#' ? 'noopener noreferrer' : undefined}
      className="block mt-4 rounded-2xl overflow-hidden group relative h-24"
      data-testid="megamenu-ad"
    >
      <div 
        className="absolute inset-0"
        style={{ backgroundColor: ad.background_color || '#1a1a2e' }}
      >
        {ad.image_url && (
          isVideoUrl(ad.image_url) ? (
            <VideoBanner
              src={ad.image_url}
              poster={ad.poster_url}
              alt={ad.title}
              className="w-full h-full object-cover"
              onError={(e) => e.target.style.display = 'none'}
            />
          ) : (
            <img
              src={ad.image_url}
              alt={ad.title}
              className="w-full h-full object-cover"
              onError={(e) => e.target.style.display = 'none'}
            />
          )
        )}
        {getOverlayOpacity(ad.overlay_opacity) > 0 && (
          <div 
            className="absolute inset-0 bg-gradient-to-r from-black to-transparent" 
            style={{ opacity: Math.min(getOverlayOpacity(ad.overlay_opacity) * 1.4, 1) }}
          />
        )}
      </div>
      <div className="absolute inset-0 p-3 flex flex-col justify-center">
        <h4 className="text-white font-bold text-sm leading-tight">{ad.title}</h4>
        {ad.button_text && (
          <span className="text-xs text-primary mt-1 font-medium">{ad.button_text} &rarr;</span>
        )}
      </div>
    </a>
  );
};

// Hook to fetch ads for a specific position
export const useAds = (position, category = null) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxAds, setMaxAds] = useState(5);
  const { currentIsland } = useIsland();

  const fetchAds = useCallback(async () => {
    try {
      // First fetch placement settings to get the max ads limit
      let limit = 5;
      try {
        const settingsRes = await fetch(`${API}/placement-settings/${position}`);
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          limit = settings.max_ads || 5;
          setMaxAds(limit);
        }
      } catch (e) {
        // Use default limit
      }
      
      const params = new URLSearchParams();
      params.append('position', position);
      params.append('limit', limit.toString());
      if (currentIsland) {
        params.append('island', currentIsland);
      }
      // Pass category for targeted ads
      if (category) {
        params.append('category', category);
      }
      
      const response = await fetch(`${API}/banners?${params}`);
      if (response.ok) {
        const data = await response.json();
        return data.filter(ad => ad.active);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
    }
    return [];
  }, [position, currentIsland, category]);

  useEffect(() => {
    const loadAds = async () => {
      setLoading(true);
      const fetchedAds = await fetchAds();
      setAds(fetchedAds);
      setLoading(false);
    };
    loadAds();
  }, [fetchAds]);

  return { ads, loading, maxAds };
};

// Small Ads Section - Only shows ads from database, nothing if none available
export const SmallPromosSection = ({ position = 'sidebar', count = 2, category = null }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configuredCount, setConfiguredCount] = useState(count);
  const { currentIsland } = useIsland();

  useEffect(() => {
    const abortController = new AbortController();
    
    // Reset state when category changes to prevent stale ads
    setAds([]);
    setLoading(true);

    const fetchAds = async () => {
      try {
        // Fetch placement settings first
        let maxAds = count;
        try {
          const settingsRes = await fetch(`${API}/placement-settings/${position}`, {
            signal: abortController.signal
          });
          if (settingsRes.ok) {
            const settings = await settingsRes.json();
            maxAds = settings.max_ads || count;
            setConfiguredCount(maxAds);
          }
        } catch (e) {
          if (e.name === 'AbortError') return;
          // Use default count
        }
        
        const params = new URLSearchParams();
        params.append('position', position);
        params.append('limit', maxAds.toString());
        if (currentIsland) {
          params.append('island', currentIsland);
        }
        // Pass category for targeted ads
        if (category) {
          params.append('category', category);
        }
        
        const response = await fetch(`${API}/banners?${params}`, {
          signal: abortController.signal
        });
        if (response.ok) {
          const data = await response.json();
          const activeAds = data.filter(ad => ad.active);
          setAds(activeAds);
        } else {
          setAds([]);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error fetching ads:', error);
        setAds([]);
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchAds();
    
    return () => abortController.abort();
  }, [position, currentIsland, category, count]);

  // Don't render anything if no ads
  if (loading) return null;
  if (ads.length === 0) return null;

  return (
    <section className="py-8 md:py-12" data-testid="small-ads-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <SmallPromoCarousel ads={ads} count={Math.min(configuredCount, ads.length)} placementId={position} />
      </div>
    </section>
  );
};

// Large Ad Section - Only shows ads from database, nothing if none assigned to position
export const LargePromoSection = ({ position = 'home_top', category = null }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentIsland } = useIsland();

  useEffect(() => {
    const abortController = new AbortController();
    
    // Reset state when category changes to prevent stale ads
    setAds([]);
    setLoading(true);

    const fetchAds = async () => {
      try {
        // Fetch placement settings first to get max ads limit
        let maxAds = 5;
        try {
          const settingsRes = await fetch(`${API}/placement-settings/${position}`, {
            signal: abortController.signal
          });
          if (settingsRes.ok) {
            const settings = await settingsRes.json();
            maxAds = settings.max_ads || 5;
          }
        } catch (e) {
          if (e.name === 'AbortError') return;
          // Use default limit
        }
        
        const params = new URLSearchParams();
        params.append('position', position);
        params.append('limit', maxAds.toString());
        if (currentIsland) {
          params.append('island', currentIsland);
        }
        // Pass category for targeted ads
        if (category) {
          params.append('category', category);
        }
        
        const response = await fetch(`${API}/banners?${params}`, {
          signal: abortController.signal
        });
        if (response.ok) {
          const data = await response.json();
          const activeAds = data.filter(ad => ad.active);
          setAds(activeAds);
        } else {
          setAds([]);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error fetching ads:', error);
        setAds([]);
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchAds();
    
    return () => abortController.abort();
  }, [position, currentIsland, category]);

  // Don't render anything if no ads assigned to this position
  if (loading) return null;
  if (ads.length === 0) return null;

  return (
    <section className="py-8 md:py-12" data-testid={`large-ad-section-${position}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <LargePromoCarousel ads={ads} placementId={position} />
      </div>
    </section>
  );
};

// Single Small Carousel with swipe support (for double banner layout)
const SingleSmallCarousel = ({ ads, placementId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const pauseTimeoutRef = useRef(null);

  useEffect(() => {
    if (isPaused || userInteracted || ads.length <= 1) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % ads.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [ads.length, isPaused, userInteracted]);

  // Reset userInteracted after 8 seconds of no interaction
  useEffect(() => {
    if (userInteracted) {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
      pauseTimeoutRef.current = setTimeout(() => {
        setUserInteracted(false);
      }, 8000);
    }
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, [userInteracted]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + ads.length) % ads.length);
    setUserInteracted(true);
  }, [ads.length]);
  
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % ads.length);
    setUserInteracted(true);
  }, [ads.length]);

  // Callback for when user swipes - pause auto-rotation
  const handleSwipeInteraction = useCallback(() => {
    setUserInteracted(true);
  }, []);

  // Swipe gesture support with interaction callback
  const swipeHandlers = useSwipeGesture(goToNext, goToPrev, handleSwipeInteraction);

  if (ads.length === 0) return null;

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div 
        className="overflow-hidden rounded-2xl relative touch-pan-y"
        {...swipeHandlers}
      >
        <div 
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {ads.map((ad, index) => (
            <div key={ad.id} className="flex-none w-full min-w-0">
              <SmallPromoBlock ad={ad} isVisible={true} placementId={placementId} />
            </div>
          ))}
        </div>
      </div>

      {ads.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {ads.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                setUserInteracted(true);
              }}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                index === currentIndex 
                  ? 'w-6 h-2 bg-primary' 
                  : 'w-2 h-2 bg-muted-foreground/40 hover:bg-muted-foreground/60'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Double Banner Section - 2 carousels side by side (left & right)
// This is the "Homepage Middle" section
export const DoubleBannerSection = ({ position = 'home_middle', category = null }) => {
  const [leftAds, setLeftAds] = useState([]);
  const [rightAds, setRightAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxAds, setMaxAds] = useState(4); // Default, will be fetched from settings
  const { currentIsland } = useIsland();

  useEffect(() => {
    const fetchAds = async () => {
      try {
        // First fetch placement settings to get max_ads
        let adsPerCarousel = 4; // Default
        try {
          const settingsRes = await fetch(`${API}/placement-settings/${position}`);
          if (settingsRes.ok) {
            const settings = await settingsRes.json();
            adsPerCarousel = settings.max_ads || 4;
            setMaxAds(adsPerCarousel);
          }
        } catch (e) {
          // Use default
        }

        const params = new URLSearchParams();
        params.append('position', position);
        // Fetch enough ads for both carousels (max_ads per side)
        params.append('limit', (adsPerCarousel * 2).toString());
        if (currentIsland) {
          params.append('island', currentIsland);
        }
        if (category) {
          params.append('category', category);
        }
        
        const response = await fetch(`${API}/banners?${params}`);
        if (response.ok) {
          const data = await response.json();
          const activeAds = data.filter(ad => ad.active);
          
          // Split ads by their side assignment (default to left if not specified)
          // Admin sets `double_position: 'left' | 'right'` on each banner
          // Use max_ads from placement settings per carousel
          const left = activeAds.filter(ad => ad.double_position !== 'right').slice(0, adsPerCarousel);
          const right = activeAds.filter(ad => ad.double_position === 'right').slice(0, adsPerCarousel);
          
          setLeftAds(left);
          setRightAds(right);
        } else {
          setLeftAds([]);
          setRightAds([]);
        }
      } catch (error) {
        console.error('Error fetching double banner ads:', error);
        setLeftAds([]);
        setRightAds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, [currentIsland, category, position]);

  // Don't render if no ads at all
  if (loading) return null;
  if (leftAds.length === 0 && rightAds.length === 0) return null;

  return (
    <section className="py-8 md:py-12" data-testid={`double-banner-section-${position}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {/* Left Carousel */}
          <div>
            {leftAds.length > 0 ? (
              <SingleSmallCarousel ads={leftAds} placementId={`${position}_left`} />
            ) : (
              <div className="aspect-[2/1] rounded-2xl bg-secondary/30 border border-dashed border-border flex items-center justify-center">
                <span className="text-sm text-muted-foreground">Ad Space Available</span>
              </div>
            )}
          </div>
          
          {/* Right Carousel */}
          <div>
            {rightAds.length > 0 ? (
              <SingleSmallCarousel ads={rightAds} placementId={`${position}_right`} />
            ) : (
              <div className="aspect-[2/1] rounded-2xl bg-secondary/30 border border-dashed border-border flex items-center justify-center">
                <span className="text-sm text-muted-foreground">Ad Space Available</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export { SmallPromoBlock, SmallPromoCarousel, LargePromoSlide, LargePromoCarousel, SingleSmallCarousel };
export default SmallPromosSection;
